import React, { useEffect, useRef } from 'react';
import {
	fetchProducts,
	getCatalogData,
	saveSort,
} from '@fh-components/fh-js-api-core/store/slices/catalogSlice';
import { catalogSortSelector, isSessionLoadedSelector, userSelector } from '@/store';
import { getCategoryTreeBySlug } from '@fh-components/fh-js-api-core/store/slices/categoriesSlice';
import qs from 'qs';
import redirectWithSavedSort from '@/lib/redirectWithSavedSort';
import redirectFirstPage from '@/lib/redirectFirstPage';
import { TextPage, CatalogPage } from '@/layouts';
import { handleError } from '@/lib/handleError';
import ecommerce from '@/seo/Ecommerce';
import { PAGE_TYPE } from '@/lib/constants';
import dataLayer from '@/seo/dataLayer/DataLayer';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';

const NextPage = ({ pageType, ...props }) => {
	const pageHistory = useRef(null);
	const router = useRouter();
	const isLoaded = useSelector(isSessionLoadedSelector);
	const user = useSelector(userSelector);
	const isSalePage = props?.data?.breadcrumbs?.[0].name?.toLowerCase() === 'outlet';

	useEffect(() => {
		if (pageType === 'redirect') {
			router.push(props.data);
		}
		if (pageType === 'text-page') {
			if (props.data.slug.includes('contacts') || props.data.slug.includes('store')) {
				ecommerce.setPageType(ecommerce.PAGE_TYPES.CONTACTS);
			} else {
				ecommerce.setPageType(ecommerce.PAGE_TYPES.OTHER);
			}
		}

		if (pageType === 'catalog') {
			if (props.data.current.slug.includes('brands')) {
				ecommerce.setPageType(ecommerce.PAGE_TYPES.BRAND);
			} else {
				ecommerce.setPageType(ecommerce.PAGE_TYPES.CATEGORY);
			}
		}
	}, [pageType]);

	useEffect(() => {
		if (router.asPath && pageType && isLoaded) {
			const type = isSalePage
				? 'sale'
				: pageType === 'text-page'
				? 'info'
				: pageType === 'catalog'
				? 'category'
				: pageType;

			pageHistory.current = router.asPath;

			if (pageType === 'catalog') {
				return;
			} else {
				dataLayer.sendUserState({ user });

				dataLayer.viewPage({
					page_type: type,
					page_path: router.asPath,
					page_title:
						pageType === 'catalog'
							? props.data.current.meta_title ?? props.data.current.name ?? document.title
							: document.title,
					page_location: window.location.href,
					page_referrer: pageHistory.current ?? document.referrer,
				});
			}
		}
	}, [router.asPath, pageType, isLoaded, isSalePage, JSON.stringify(props)]);

	switch (pageType) {
		case 'catalog':
			return <CatalogPage {...props} pageHistory={pageHistory.current} />;
		case 'text-page':
			return <TextPage {...props} />;
		default:
			return null;
	}
};

NextPage.getInitialProps = async ({ query, reduxStore, res, asPath }) => {
	const {
		typeSlug,
		page: pageQuery,
		sort: selectedSorting,
		filters: selectedFilters,
		access,
	} = qs.parse(query, {
		arrayLimit: 100,
	});

	const pageOrCategorySlug = typeSlug.join('/');

	let selectedCategory;
	let data;
	let isRedirect = false;

	await reduxStore.dispatch(
		getCategoryTreeBySlug({
			slug: `/${pageOrCategorySlug}`,
			onSuccess: result => {
				if (result.page.pageType === 'redirect') {
					isRedirect = true;
					data = result.page.data.to;
				} else {
					data = result.page.data;
					selectedCategory = result.page.data.current;
				}
			},
			onError: result => {
				handleError(result);
			},
		}),
	);

	if (isRedirect) {
		return {
			pageType: 'redirect',
			data,
		};
	}

	const isCatalogLink = selectedCategory?.type === 'menu';
	if (isCatalogLink) {
		redirectFirstPage({ page: pageQuery, res, asPath });

		const savedSort = catalogSortSelector(reduxStore.getState());

		if (selectedSorting && savedSort !== selectedSorting)
			reduxStore.dispatch(saveSort(selectedSorting));
		if (savedSort && !selectedSorting) {
			redirectWithSavedSort({ res, asPath, sort: savedSort });
			return;
		}

		const sortFields = selectedCategory.sort_fields;
		const hasSelectedSortInFields = sortFields.some(field => field.value === selectedSorting);
		const params = {
			hash: selectedCategory.hash,
			page: pageQuery || 1,
			filters: selectedFilters,
			sort: hasSelectedSortInFields
				? selectedSorting
				: sortFields.length > 0
				? sortFields[0].value
				: 'new',
		};

		await reduxStore.dispatch(fetchProducts(params));

		const pageData = getCatalogData(reduxStore.getState());

		handleError(pageData.error);
	} else {
		return {
			pageType: 'text-page',
			slug: pageOrCategorySlug,
			textPageType: 'page',
			access,
			data: data.page,
		};
	}

	return { pageType: 'catalog', data };
};

export default NextPage;
